
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { PrivacyFirstScanner } from "@/components/id-verification/PrivacyFirstScanner";
import { VenueVerificationDisplay } from "@/components/id-verification/VenueVerificationDisplay";
import { Shield, UserCheck, AlertTriangle, Eye, Lock } from "lucide-react";

const SecurityCheckIn = () => {
  const [scannerMode, setScannerMode] = useState<'privacy-scanner' | 'venue-display' | 'manual'>('privacy-scanner');
  const [manualEntry, setManualEntry] = useState({
    patronName: '',
    documentId: '',
    notes: ''
  });
  const { toast } = useToast();

  const handleManualCheckIn = () => {
    if (!manualEntry.patronName || !manualEntry.documentId) {
      toast({
        title: "Missing Information",
        description: "Please provide patron name and document ID.",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Manual Check-in Complete",
      description: `${manualEntry.patronName} has been checked in manually.`,
    });

    setManualEntry({ patronName: '', documentId: '', notes: '' });
  };

  const handleVerificationReceived = (result: any) => {
    toast({
      title: `Verification ${result.status === 'verified' ? 'Successful' : 'Failed'}`,
      description: `Patron verification ${result.status} with ${result.confidence || 'unknown'}% confidence.`,
      variant: result.status === 'verified' ? 'default' : 'destructive'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-black p-4">
      <div className="container mx-auto max-w-6xl space-y-6">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-2">
            <Shield className="h-8 w-8 text-purple-400" />
            <h1 className="text-3xl font-bold text-white">Privacy-First Security Check-In</h1>
          </div>
          <p className="text-purple-200">Next-generation ID verification with zero data retention</p>
          
          {/* Privacy Assurance Banner */}
          <Card className="cyber-glass border-neon-green/30 max-w-2xl mx-auto">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Lock className="w-6 h-6 text-neon-green flex-shrink-0" />
                <div className="text-left">
                  <h3 className="text-white font-semibold">Australian Privacy Act Compliant</h3>
                  <p className="text-sm text-gray-300">
                    Zero data storage • On-device processing • Minimal data collection
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mode Toggle */}
        <div className="flex justify-center gap-4 flex-wrap">
          <Button
            variant={scannerMode === 'privacy-scanner' ? 'default' : 'outline'}
            onClick={() => setScannerMode('privacy-scanner')}
            className="flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            Privacy Scanner
          </Button>
          <Button
            variant={scannerMode === 'venue-display' ? 'default' : 'outline'}
            onClick={() => setScannerMode('venue-display')}
            className="flex items-center gap-2"
          >
            <UserCheck className="h-4 w-4" />
            Venue Display
          </Button>
          <Button
            variant={scannerMode === 'manual' ? 'default' : 'outline'}
            onClick={() => setScannerMode('manual')}
            className="flex items-center gap-2"
          >
            <AlertTriangle className="h-4 w-4" />
            Manual Entry
          </Button>
        </div>

        {/* Content based on mode */}
        {scannerMode === 'privacy-scanner' && (
          <div className="max-w-2xl mx-auto">
            <PrivacyFirstScanner onVerificationReceived={handleVerificationReceived} />
          </div>
        )}

        {scannerMode === 'venue-display' && (
          <VenueVerificationDisplay />
        )}

        {scannerMode === 'manual' && (
          <Card className="glass-card max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 h-5" />
                Manual Check-In (Emergency Only)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Privacy Notice:</strong> Manual check-in should only be used when privacy-first verification is not available. 
                  Always minimize data collection and ensure immediate deletion of any manually recorded information.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="patronName">Patron Name (Optional)</Label>
                  <Input
                    id="patronName"
                    value={manualEntry.patronName}
                    onChange={(e) => setManualEntry(prev => ({ ...prev, patronName: e.target.value }))}
                    placeholder="Enter name only if required"
                  />
                </div>
                <div>
                  <Label htmlFor="documentId">Document Type Only</Label>
                  <Input
                    id="documentId"
                    value={manualEntry.documentId}
                    onChange={(e) => setManualEntry(prev => ({ ...prev, documentId: e.target.value }))}
                    placeholder="e.g., Driver's License, Passport"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Verification Notes</Label>
                <Textarea
                  id="notes"
                  value={manualEntry.notes}
                  onChange={(e) => setManualEntry(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Brief verification notes (avoid personal details)"
                  rows={3}
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Privacy Reminder</h4>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  <li>• Record only the minimum information necessary</li>
                  <li>• Do not record ID numbers or personal details</li>
                  <li>• Delete this information within 24 hours</li>
                  <li>• Consider patron consent for any data collection</li>
                </ul>
              </div>

              <Button onClick={handleManualCheckIn} className="w-full">
                Complete Privacy-Compliant Manual Check-In
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Statistics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <Badge variant="secondary" className="mb-2">Today</Badge>
              <p className="text-2xl font-bold text-white">147</p>
              <p className="text-sm text-purple-200">Privacy Verifications</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <Badge variant="secondary" className="mb-2">Active</Badge>
              <p className="text-2xl font-bold text-white">89</p>
              <p className="text-sm text-purple-200">Currently Inside</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <Badge variant="secondary" className="mb-2">Compliance</Badge>
              <p className="text-2xl font-bold text-neon-green">100%</p>
              <p className="text-sm text-purple-200">Privacy Standards</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <Badge variant="secondary" className="mb-2">Data</Badge>
              <p className="text-2xl font-bold text-neon-green">0</p>
              <p className="text-sm text-purple-200">Bytes Stored</p>
            </CardContent>
          </Card>
        </div>

        {/* Technical Information */}
        <Card className="cyber-glass border-neon-purple/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Shield className="w-5 h-5" />
              Privacy-First Technology Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-neon-green mb-3">On-Device Processing</h4>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-neon-green rounded-full mt-2 flex-shrink-0"></span>
                    <span>Facial biometric comparison using WebAssembly and TensorFlow.js</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-neon-green rounded-full mt-2 flex-shrink-0"></span>
                    <span>Document authenticity verification via advanced computer vision</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-neon-green rounded-full mt-2 flex-shrink-0"></span>
                    <span>Real-time processing with sub-5-second verification times</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-neon-green mb-3">Privacy Protection</h4>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-neon-green rounded-full mt-2 flex-shrink-0"></span>
                    <span>Zero data transmission to venue or cloud servers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-neon-green rounded-full mt-2 flex-shrink-0"></span>
                    <span>Automatic deletion of all biometric data after processing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-neon-green rounded-full mt-2 flex-shrink-0"></span>
                    <span>Encrypted local processing with hardware security</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-neon-purple/20 pt-4">
              <p className="text-xs text-gray-400 text-center">
                This system is designed in accordance with Australian Privacy Principles and implements 
                privacy-by-design methodologies as required by the Privacy Act 1988.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SecurityCheckIn;
